import { Box, Button, Link, Text } from 'native-base'
import { useContext, useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { useMutation, useQuery } from '@apollo/client'
import { useGateValue } from '@statsig/react-bindings'

import { AppContext } from '../../context/AppContext'
import { AuthContext } from '../../context/AuthContext'
import {
  GET_ORDER_BY_TRACKING_ID_CWA,
  LOGIN_WITH_TOKEN,
  LoginWithTokenMutation,
  LoginWithTokenMutationVariables,
  Order,
} from '@consumer/graphql'
import { trackEvent } from '../../utils/analytics-v2'
import { STATSIG_FEATURE_GATES } from '@consumer/shared'
import { signInWithToken } from '../../utils/firebase'
import Loader from '../Loader'
import EditBusinessHours from './EditBusinessHours'

/** Page component for viewing and editing business hours associated with an order's delivery address. */
export default function BusinessHoursPage() {
  const { trackingId } = useParams()
  const [searchParams] = useSearchParams()
  const { order, setOrder } = useContext(AppContext)
  const { firebaseUser } = useContext(AuthContext)
  const navigate = useNavigate()
  const includeConsumerSupportCases = useGateValue(STATSIG_FEATURE_GATES.cwa_support_escalation)

  const { loading, error, data } = useQuery<{
    order: Order
  }>(GET_ORDER_BY_TRACKING_ID_CWA, {
    variables: { trackingId, includeConsumerSupportCases },
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    skip: !!searchParams.get('token'), // Skip first load if token is present
    onError: error => {
      // Track errors except for authorization errors (which are expected when not logged in)
      if (!error.message.includes('Not Authorized') && !error.message.includes('401')) {
        trackEvent('track_package_error', {
          error_reason: 'unknown',
          tracking_code: trackingId ?? '',
          features: 'cwa_map_view',
          page_type: 'business_hours',
        })
      }
    },
  })

  const [loginWithTokenMutation, { loading: tokenLoading }] = useMutation<
    LoginWithTokenMutation,
    LoginWithTokenMutationVariables
  >(LOGIN_WITH_TOKEN, {
    errorPolicy: 'all',
    onCompleted: async data => {
      if (data?.loginWithToken) {
        try {
          await signInWithToken(data.loginWithToken)
          trackEvent('token_login_success', { features: 'cwa_token_auth' })
        } catch {
          trackEvent('token_login_error', { error_reason: 'firebase_error', features: 'cwa_token_auth' })
        }
      }
      // After signing in, navigate to clean URL without token
      if (trackingId) {
        navigate(`/trackingId/${trackingId}/hours`, { replace: true })
      }
    },
    onError: () => {
      trackEvent('token_login_error', { error_reason: 'internal_error', features: 'cwa_token_auth' })
      // If sign in fails, navigate to clean URL without token
      if (trackingId) {
        navigate(`/trackingId/${trackingId}/hours`, { replace: true })
      }
    },
    // Refetch the order query after login with token
    refetchQueries: [GET_ORDER_BY_TRACKING_ID_CWA],
  })

  useEffect(() => {
    if (data?.order) {
      trackEvent('track_package', {
        tracking_code: trackingId ?? '',
        features: 'cwa_map_view',
        veho_client_id: data?.order?.clientId ?? '',
        is_authenticated: !!firebaseUser,
        page_type: 'business_hours',
      })
      setOrder(data.order as Order)
    }
  }, [data, setOrder, trackingId, firebaseUser])

  useEffect(() => {
    if (!trackingId) {
      return
    }

    const token = searchParams.get('token')

    // If token is present and user is not logged in, login with token
    if (token && !firebaseUser) {
      loginWithTokenMutation({ variables: { token } })
      return
    }

    // If token is present and user is logged in, navigate to clean URL without token
    if (token && firebaseUser) {
      navigate(`/trackingId/${trackingId}/hours`, { replace: true })
    }
  }, [trackingId, searchParams, firebaseUser, loginWithTokenMutation, navigate])

  // Check if address type supports business hours
  const businessHoursDisabled = !loading && !tokenLoading && order && !order.address?.hours?.enabled

  return (
    <Box width="100%" maxW="1200px" mx="auto" px={3} py={3}>
      {(loading || tokenLoading) && <Loader />}

      {/* General order fetch error (excluding auth errors) */}
      {error &&
        !error.message.includes('Not Authorized') &&
        !error.message.includes('401') &&
        !error?.message?.includes(
          `Cannot return null for non-nullable type: 'String' within parent 'DeliveryDate' (/order/deliveryDateInfo/date)`
        ) && (
          <Box
            backgroundColor="red.50"
            padding={6}
            borderRadius={8}
            marginBottom={4}
            display="flex"
            flexDirection="column"
            alignItems="center"
          >
            <Text color="red.800" fontSize="md" marginBottom={4} textAlign="center">
              Looks like there was an issue fetching the order information. Please try reloading the page.
            </Text>
            <Button onPress={() => window.location.reload()} borderRadius={100} bgColor="black" width="250px">
              Reload Page
            </Button>
          </Box>
        )}

      {/* Address doesn't support business hours */}
      {businessHoursDisabled && (
        <Box
          backgroundColor="yellow.50"
          padding={6}
          borderRadius={8}
          marginBottom={4}
          display="flex"
          flexDirection="column"
          alignItems="center"
        >
          <Text color="yellow.800" fontSize="md" marginBottom={2} textAlign="center" fontWeight="bold">
            Business Hours Not Available
          </Text>
          <Text color="yellow.800" fontSize="sm" textAlign="center">
            Business hours are not supported for this address. If you need to set business hours, please text{' '}
            <Link href="sms:68953">68953</Link> to contact support.
          </Text>
        </Box>
      )}

      {/* Order not found */}
      {!loading && !error && data?.order === null && (
        <Box
          backgroundColor="red.50"
          padding={6}
          borderRadius={8}
          display="flex"
          flexDirection="column"
          alignItems="center"
        >
          <Text color="red.800" fontSize="md" textAlign="center">
            We can't find a package associated with this tracking number. Please double check your tracking ID. If you
            need help, text <Link href="sms:68953">68953</Link> to contact support.
          </Text>
        </Box>
      )}

      {/* Success: Show business hours component */}
      {!loading && !tokenLoading && order?.address?.hours && !businessHoursDisabled && (
        <EditBusinessHours
          normalizedAddressId={order.address.normalizedAddressId!}
          orderId={order.orderId}
          initialHours={order.address.hours}
          onSave={updatedHours => {
            trackEvent('save_business_hours', {
              veho_client_id: order.clientId ?? '',
              is_authenticated: !!firebaseUser,
            })
            // Update the order with the new hours
            setOrder({ ...order, address: { ...order.address, hours: updatedHours } })
            // Navigate back to the tracking page
            navigate(`/trackingId/${trackingId}`)
          }}
          onCancel={() => {
            trackEvent('save_business_hours_cancel', {
              veho_client_id: order.clientId ?? '',
              is_authenticated: !!firebaseUser,
            })
            // Navigate back to the tracking page
            navigate(`/trackingId/${trackingId}`)
          }}
        />
      )}
    </Box>
  )
}
